import React, { useEffect, useState, useMemo } from 'react'
import { Table, DataTable, Chip, Checkbox, Button } from '@arnsbogroup/react-voicearchive'
import { Span, Timestamp, FilesReadyForProduction } from 'components/datatable'
import { useIntl } from 'react-intl' 
import classNames from 'classnames' 
import SupplierTaskPage from 'containers/SupplierTaskPage'
import messages from './messages'

const SupplierJobOverviewPage = ({ history, ...props }) => {
  const [tasks, setTasks] = useState([])
  const { formatMessage } = useIntl()
  const [showHiddenTasks, setShowHiddenTasks] = useState(false);
  const [checkedTasks, setCheckedTasks] = useState([]);
  const [showTask, setShowTask] = useState(null);
  const [sorting, setSorting] = useState({
    key: 'deadline',
    direction: 'DESC',
  })

  useEffect(() => {
    fetch('/supplierviewdisplay/overviewTasks')
    .then((response) => response.json())
    .then(response => {
      if (response.success) {
        setTasks(response.data)
      } else {
          toastr.error(response.errors)
      }
    })
  },[])

  const getTaskState = ({ hidden, done, supplierapproval }) => {
    let taskState = {};
    if (+hidden) {
      taskState = {
        state: 'error',
        text: formatMessage(messages.hiddenState)
      }
    } else if (+done) {
      taskState = {
        state: 'success',
        text: formatMessage(messages.doneState)
      }
    } else if (+supplierapproval) {
      taskState = {
        state: 'info',
        text: formatMessage(messages.inProgressState)
      }
    } else {
      return null
    }
    return (
      <div className='w-full text-center'>
        <Chip size="sm" state={taskState.state}>
          <span className="capitalize">
            { taskState.text }
          </span>
        </Chip>
      </div>
    )
  }

  const checkAllTasks = () => {
    if (checkedTasks.length === tasks.length || 0) {
      setCheckedTasks([])
    } else {
      setCheckedTasks(tasks.map((task) => {
        return task.uid
      }))
    }
  };

  const checkTask = (uid) => {
    if (checkedTasks.includes(uid)) {
      setCheckedTasks(checkedTasks.filter((taskUid) => taskUid !== uid));
    } else {
      setCheckedTasks([...checkedTasks, uid]);
    }
  };

  const toggleTasks = (hide) => {
    if (!checkedTasks.length) {
      toastr.error('Please select tasks')
      return
    }
    var data = new FormData();
    data.append('taskIds[]', checkedTasks);
    data.append('hide', hide);

    fetch('/supplierviewdisplay/toggleTasks.json', {
      method: 'POST',
      body: data
    })
    .then((response) => response.json())
    .then((response) => {
      if (response.success) {
        setTasks((prev) => {
          return prev.map((prevTask) => {
            if (checkedTasks.find(checkedTask => checkedTask == prevTask.uid)) {
              prevTask.hidden = hide
            }
            return prevTask
          })
        })
        setCheckedTasks([]);
      }
    })
  }

  const getDueTodayTask = () => {
    const now = (new Date()).setHours(0,0,0,0);
    return tasks.filter((task) => {
      const taskDeadline = new Date(parseInt(task.deadline) * 1000).setHours(0,0,0,0);
      return taskDeadline == now
    }).length
  }

  const getDueTomorrowTask = () => {
    const now = new Date()
    let tomorrow = ((new Date()).setDate(now.getDate() + 1));
    tomorrow = (new Date(tomorrow)).setHours(0,0,0,0)
    return tasks.filter((task) => {
      const taskDeadline = new Date(parseInt(task.deadline) * 1000).setHours(0,0,0,0);
      return taskDeadline == tomorrow
    }).length
  }
  
  const getDuePastFutureTask = () => {
    const now = (new Date()).setHours(0,0,0,0)
    return tasks.filter((task) => {
      const taskDeadline = new Date(parseInt(task.deadline) * 1000).setHours(0,0,0,0);
      return !(+task.hidden) && taskDeadline != now
    }).length
  }
  
  const getIndicatorColor = (color) => {
    return {
      red: 'bg-red-100',
      green: 'bg-green-100',
      yellow: 'bg-yellow-100',
      grey: 'bg-grey-100',
    }[color || 'bg-grey-100']
  }

  const entries = useMemo(() => {
    return tasks?.sort((a, b) => {
      const valueA = isNaN(parseInt(a[sorting.key])) ? a[sorting.key] : parseInt(a[sorting.key])
      const valueB = isNaN(parseInt(b[sorting.key])) ? b[sorting.key] : parseInt(b[sorting.key])

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        if (sorting.direction === 'DESC') {
          return valueB.localeCompare(valueA)
        } else {
          return valueA.localeCompare(valueB)
        }
      } else {
        if (sorting.direction === 'DESC') {
          return valueB - valueA
        } else {
          return valueA - valueB
        }
      }
    }) || []
  }, [tasks, sorting])

  return (
    <div>
      <div className='flex justify-between m-2'>
          {!showTask && 
            <div className='flex items-center px-4 py-2 space-x-2'>
              {!!checkedTasks.length && 
                <>
                  <Button 
                    state="negative"
                    size="xs"
                    onClick={() => toggleTasks(true)}
                  >
                    {formatMessage(messages.hideJobsButton)}
                  </Button>

                  <Button
                    size="xs"
                    onClick={() => toggleTasks(false)}
                  >
                    {formatMessage(messages.unhideJobsButton)}
                  </Button>
                </>
              }

              <Button
                size="xs"
                onClick={() => setShowHiddenTasks(!showHiddenTasks)}
              >
                {showHiddenTasks ? formatMessage(messages.hideHiddenJobsButton) : formatMessage(messages.showHiddenJobsButton)}
              </Button>
            </div>
          }
          {!!showTask && 
          <div className='text-center'> 
            <Button
              size="xs"
              onClick={() => setShowTask(null)}
            >
              {formatMessage(messages.showFullListButton)}
            </Button>
          </div>
        }
        <div className="text-gray-600 space-x-2 px-4 py-2 text-center">
          <span> {getDueTodayTask()} {formatMessage(messages.dueToday)}</span>
          <span>{getDuePastFutureTask()} {formatMessage(messages.pastFutureJobs)} ({getDueTomorrowTask()} {formatMessage(messages.dueTomorrow)})</span>
        </div>
        
      </div>
      <div className='flex'>
        <div className={classNames('flex overflow-scroll flex-col h-full', !!showTask ? 'w-1/4 border-r border-gray-100' : 'w-full')}>
          <DataTable
            className="table-fixed"
            indicators={[
              FilesReadyForProduction
            ]}
            components={{
              Tr: ({ childProps = {}, ...innerProps }) => {
                return (
                  <Table.Tr
                    {...innerProps }
                    className={classNames({
                      'cursor-pointer': !!childProps,
                      'hidden': +childProps?.hidden && !showHiddenTasks,
                    }, childProps?.uid == showTask ? 'bg-blue-50' : getIndicatorColor(childProps.ordertaskstatus_indicator))}
                    onClick={childProps?.uid ? () => setShowTask(childProps.uid) : undefined}
                  />
                )
              },
              Td: ({ childProps = {}, ...props }) => (
                <Table.Td
                  {...props }
                />
              )
            }}
            head={{
              checkbox: { className: 'w-12', children: (
                <div>
                  <Checkbox 
                    checked={checkedTasks.length === !!tasks.length ? tasks.length : 0}
                    onChange={() => {
                      checkAllTasks()
                    }}
                  />
                </div>
              ) },
              uid: { sortable: true, label: formatMessage(messages.idLabel), className: 'w-24' },
              deadline: { sortable: true, label: formatMessage(messages.deadlineLabel), className: 'w-40' },
              state: { label: formatMessage(messages.stateLabel), className: 'w-32 text-center' },
              ordertaskstatus_name: { sortable: true, label: formatMessage(messages.statusLabel) },
              recordings: { label: formatMessage(messages.recordingsLabel), className: 'w-28' },
              title: { label: formatMessage(messages.titleLabel) },
            }}
            body={!!showTask ? {
              deadline: ({ deadline }) => (
                <div className='flex'>
                  <Timestamp includeStatus>{deadline}</Timestamp>
                </div>
              )
            } : {
              checkbox: ({uid}) => (
                <Checkbox
                  onClick={(e) => e.stopPropagation()}
                  checked={checkedTasks.some((taskId) => taskId == uid)}
                  onChange={() => {checkTask(uid)}}
                />
              ),
              uid: Span,
              deadline: ({ deadline }) => (
                <div className='flex'>
                  <Timestamp includeStatus>{deadline}</Timestamp>
                </div>
              ),
              state: getTaskState,
              ordertaskstatus_name: Span,
              recordings: ({ recordings }) => (
                  <Span>{recordings} {formatMessage(messages.recordingsLabel)}</Span>
              ),
              title: Span 
            }}
            entries={entries}
            sorting={sorting}
            setSorting={setSorting}
          />
        </div>
        {!!showTask && 
          <div className='w-full'> 
            <SupplierTaskPage taskId={showTask}/> 
          </div>
        }
      </div>
    </div>
  )
}

export default SupplierJobOverviewPage
